import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck } from 'lucide-react';
import { Button } from '@dzseguidores/ui';
import { useSeo } from '@/hooks/use-seo';
import { useProducts } from '@/hooks/use-catalog';
import { useCroSettings, usePublicFaqs } from '@/hooks/use-cro-settings';
import { FeaturedCatalog } from '@/components/cro/featured-catalog';
import { TrustBar } from '@/components/cro/trust-bar';
import { ScarcityNote } from '@/components/cro/social-proof';
import { api } from '@/lib/api-client';
import {
  HOME_FAQS,
  SITE,
  faqJsonLd,
  itemListJsonLd,
  organizationJsonLd,
  websiteJsonLd,
} from '@/lib/seo';
import type { ProductPublic } from '@dzseguidores/types';

type Network = { id: string; name: string; slug: string };

export function HomePage() {
  const [networkFilter, setNetworkFilter] = useState('');
  const [networks, setNetworks] = useState<Network[]>([]);
  const { data, isLoading } = useProducts(networkFilter || undefined);
  const { data: cro } = useCroSettings();
  const { data: cmsFaqs } = usePublicFaqs();
  const products: ProductPublic[] = data?.items ?? [];
  const faqs =
    cmsFaqs && cmsFaqs.length > 0
      ? cmsFaqs.map((f) => ({ q: f.question, a: f.answer }))
      : [...HOME_FAQS];

  useSeo({
    title: SITE.defaultTitle,
    description: SITE.defaultDescription,
    path: '/',
    jsonLd: [
      organizationJsonLd(),
      websiteJsonLd(),
      faqJsonLd(faqs),
      ...(products.length
        ? [
            itemListJsonLd(
              products.slice(0, 8).map((p, i) => ({
                name: p.name,
                slug: p.slug,
                position: i + 1,
              })),
              'Pacotes de seguidores e curtidas',
            ),
          ]
        : []),
    ],
  });

  useEffect(() => {
    api
      .get<Network[]>('/catalog/networks?withProducts=true')
      .then(setNetworks)
      .catch(() => setNetworks([]));
  }, []);

  const scarcityRaw = cro?.['cro.scarcity'];
  const scarcity = typeof scarcityRaw === 'string' ? scarcityRaw : '';

  return (
    <div className="relative overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(ellipse_120%_80%_at_50%_-30%,var(--color-glow),transparent_60%)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 -z-10 opacity-[0.03] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(var(--color-fg) 1px, transparent 1px), linear-gradient(90deg, var(--color-fg) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
        }}
      />

      <section className="container-page flex min-h-[calc(100vh-4rem)] flex-col justify-center py-16 md:py-24">
        <motion.p
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-4 font-display text-sm font-semibold uppercase tracking-[0.2em] text-muted"
        >
          DZ Seguidores
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="max-w-3xl text-4xl font-semibold leading-[1.08] sm:text-5xl lg:text-6xl"
        >
          Compre seguidores e curtidas no Instagram e TikTok.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.12 }}
          className="mt-5 max-w-xl text-lg text-muted"
        >
          Likes e seguidores com entrega monitorada, PIX seguro e garantia de reposição — suporte
          humano em português.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <a href="#pacotes">
            <Button size="lg">
              Ver pacotes
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </a>
          <Link to="/instagram">
            <Button variant="secondary" size="lg">
              Instagram
            </Button>
          </Link>
          <Link to="/tiktok">
            <Button variant="secondary" size="lg">
              TikTok
            </Button>
          </Link>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35 }}
          className="mt-12 flex items-center gap-2 text-sm text-muted"
        >
          <ShieldCheck className="h-4 w-4" />
          Pagamento via PIX ou cartão · Garantia de reposição
        </motion.div>
      </section>

      <section className="container-page border-t border-border py-14 md:py-16">
        <FeaturedCatalog
          products={products}
          isLoading={isLoading}
          title="Catálogo em destaque"
          intro="Pacotes de seguidores e curtidas com preço claro — escolha e compre agora."
          limit={6}
          networkFilter={networkFilter}
          onNetworkChange={setNetworkFilter}
          networks={networks.map((n) => ({ slug: n.slug, name: n.name }))}
        />
        {scarcity ? (
          <div className="mt-8 max-w-xl">
            <ScarcityNote text={scarcity} />
          </div>
        ) : null}
      </section>

      <section className="container-page border-t border-border py-14 md:py-16">
        <h2 className="text-2xl font-semibold sm:text-3xl">Por que comprar aqui</h2>
        <p className="mt-2 max-w-xl text-muted">
          Menos fricção no checkout, mais confiança na entrega.
        </p>
        <TrustBar className="mt-8" />
      </section>

      <section className="container-page border-t border-border py-14 md:py-16">
        <h2 className="max-w-2xl text-2xl font-semibold sm:text-3xl">
          Instagram ou TikTok — comece pelo serviço certo
        </h2>
        <p className="mt-3 max-w-xl text-muted">
          Landing pages otimizadas para quem busca seguidores ou curtidas em cada rede.
        </p>
        <div className="mt-10 grid gap-8 md:grid-cols-2">
          <div>
            <h3 className="text-lg font-semibold">Instagram</h3>
            <p className="mt-2 text-sm text-muted">
              Compre seguidores Instagram e curtidas para posts e Reels. Prova social rápida, sem
              senha.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <Link to="/instagram/seguidores" className="underline hover:text-fg">
                Seguidores Instagram
              </Link>
              <Link to="/instagram/curtidas" className="underline hover:text-fg">
                Curtidas Instagram
              </Link>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold">TikTok</h3>
            <p className="mt-2 text-sm text-muted">
              Compre seguidores TikTok e likes para vídeos. Mais engajamento com entrega monitorada.
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-sm">
              <Link to="/tiktok/seguidores" className="underline hover:text-fg">
                Seguidores TikTok
              </Link>
              <Link to="/tiktok/curtidas" className="underline hover:text-fg">
                Curtidas TikTok
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="container-page border-t border-border py-14 md:py-16">
        <h2 className="text-2xl font-semibold sm:text-3xl">Como funciona</h2>
        <ol className="mt-8 grid gap-6 sm:grid-cols-3">
          {[
            {
              step: '1',
              title: 'Escolha o pacote',
              text: 'Seguidores ou curtidas no Instagram ou TikTok — volume e preço claros.',
            },
            {
              step: '2',
              title: 'Informe o link',
              text: 'Só o perfil ou a publicação pública. Nunca pedimos senha.',
            },
            {
              step: '3',
              title: 'Pague e acompanhe',
              text: 'PIX ou cartão. Entrega monitorada com garantia de reposição.',
            },
          ].map((item) => (
            <li key={item.step}>
              <p className="font-display text-sm font-semibold text-muted">Passo {item.step}</p>
              <h3 className="mt-1 font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-muted">{item.text}</p>
            </li>
          ))}
        </ol>
      </section>

      <section className="container-page border-t border-border py-14 md:py-16">
        <h2 className="text-2xl font-semibold sm:text-3xl">Perguntas frequentes</h2>
        <div className="mt-6 max-w-3xl divide-y divide-border rounded-xl border border-border">
          {faqs.map((f) => (
            <details key={f.q} className="group px-4 py-3">
              <summary className="cursor-pointer list-none font-medium">{f.q}</summary>
              <p className="mt-2 pb-2 text-sm text-muted">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-border glass p-3 md:hidden">
        <a href="#pacotes" className="block">
          <Button className="w-full" size="lg">
            Ver pacotes e preços
          </Button>
        </a>
      </div>
      <div className="h-16 md:hidden" aria-hidden />
    </div>
  );
}
